/**
 * Dispute Management API Hooks
 * React hooks for managing dispute data
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { riderApiClient } from './riderApiClient';
import {
  DisputeDTO,
  DisputeListParams,
  DisputeStats,
  ResolveDisputeRequest,
  RejectDisputeRequest,
} from './disputeTypes';
import { ApiResponse, PagedData } from './types';

/**
 * Hook to fetch a single dispute by ID
 * @param id - Dispute ID
 * @param options - Hook options
 */
export function useDispute(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DisputeDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const response = await riderApiClient.get<ApiResponse<DisputeDTO>>(`/dispute/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [id]);

  const query = useQuery<DisputeDTO>({
    queryKey: ['dispute', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch paginated list of disputes
 * @param params - Query parameters (page, size, sort, status, disputeType)
 * @param options - Hook options
 */
export function useDisputes(
  params: DisputeListParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<DisputeDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    // Build pageable object
    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.status) {
      queryParams.status = params.status;
    }
    if (params.disputeType) {
      queryParams.disputeType = params.disputeType;
    }

    const response = await riderApiClient.get<ApiResponse<PagedData<DisputeDTO>>>(
      '/dispute/list',
      queryParams
    );

    const raw = (response as any)?.data ?? response;

    // Normalize paged response: backend may use content, data, or list for the array
    const content = Array.isArray(raw)
      ? raw
      : raw?.content ?? raw?.data ?? raw?.list ?? [];
    const totalElements = raw?.totalElements ?? raw?.total ?? raw?.totalCount ?? content.length;
    const size = params.size ?? content.length;
    const totalPages = raw?.totalPages ?? (size > 0 ? Math.ceil(totalElements / size) : 1);
    const pageNumber = raw?.page?.number ?? raw?.number ?? raw?.page ?? 0;
    const pageSize = raw?.page?.size ?? raw?.size ?? content.length;

    return {
      content,
      totalElements,
      totalPages,
      first: pageNumber === 0,
      last: pageNumber >= totalPages - 1,
      empty: content.length === 0,
      page: { number: pageNumber, size: pageSize },
    } as PagedData<DisputeDTO>;
  }, [params.page, params.size, params.sort, params.status, params.disputeType]);

  const query = useQuery<PagedData<DisputeDTO>>({
    queryKey: ['disputes', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch disputes by user (rider/driver)
 * @param userId - User ID
 * @param userType - User type (DRIVER, RIDER, OTHER, ADMIN)
 * @param status - Optional status filter
 * @param options - Hook options
 */
export function useDisputesByUser(
  userId: number,
  userType: 'DRIVER' | 'RIDER' | 'OTHER' | 'ADMIN',
  status?: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED',
  options: {
    enabled?: boolean;
    onSuccess?: (data: DisputeDTO[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};
    if (status) {
      queryParams.status = status;
    }

    const response = await riderApiClient.get<ApiResponse<DisputeDTO[]>>(
      `/dispute/user/${userType}/${userId}`,
      queryParams
    );

    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [userId, userType, status]);

  return useQuery<DisputeDTO[]>({
    queryKey: ['disputes-by-user', userId, userType, status],
    queryFn,
    enabled: options.enabled !== false && !!userId,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch dispute by booking ID
 * @param bookingId - Booking ID
 * @param options - Hook options
 */
export function useDisputeByBooking(
  bookingId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DisputeDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const response = await riderApiClient.get<ApiResponse<DisputeDTO>>(
      `/dispute/booking/${bookingId}`
    );

    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [bookingId]);

  return useQuery<DisputeDTO>({
    queryKey: ['dispute-by-booking', bookingId],
    queryFn,
    enabled: options.enabled !== false && !!bookingId,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch dispute dashboard stats
 * @param options - Hook options
 */
export function useDisputeStats(
  options: {
    enabled?: boolean;
    onSuccess?: (data: DisputeStats) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const response = await riderApiClient.get<ApiResponse<DisputeStats>>('/dispute/stats');

    const raw = (response as any)?.data ?? response;
    if (!raw || typeof raw !== 'object') return raw;

    // Normalize stats: backend may use resolvedDisputes, resolved, or nested data
    const stats: DisputeStats = {
      totalDisputes: raw.totalDisputes ?? raw.total,
      openDisputes: raw.openDisputes ?? raw.open,
      inProgressDisputes: raw.inProgressDisputes ?? raw.inProgress,
      resolvedDisputes: raw.resolvedDisputes ?? raw.resolved,
      rejectedDisputes: raw.rejectedDisputes ?? raw.rejected,
      averageResolutionTime: raw.averageResolutionTime,
      disputesByType: raw.disputesByType,
      disputesByPriority: raw.disputesByPriority,
    };
    return stats;
  }, []);

  const query = useQuery<DisputeStats>({
    queryKey: ['dispute-stats'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to create a new dispute
 * @param options - Hook options
 */
export function useCreateDispute(
  options: {
    onSuccess?: (data: ApiResponse<DisputeDTO>, variables: DisputeDTO) => void;
    onError?: (error: Error, variables: DisputeDTO) => void;
  } = {}
) {
  return useMutation<ApiResponse<DisputeDTO>, Error, DisputeDTO>({
    mutationFn: async (disputeData: DisputeDTO) => {
      const response = await riderApiClient.post<ApiResponse<DisputeDTO>>('/dispute', disputeData);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update an existing dispute
 * @param options - Hook options
 */
export function useUpdateDispute(
  options: {
    onSuccess?: (
      data: ApiResponse<DisputeDTO>,
      variables: { id: number; data: DisputeDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: DisputeDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DisputeDTO>, Error, { id: number; data: DisputeDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: DisputeDTO }) => {
      const response = await riderApiClient.put<ApiResponse<DisputeDTO>>(`/dispute/${id}`, data);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to delete a dispute
 * @param options - Hook options
 */
export function useDeleteDispute(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await riderApiClient.delete<ApiResponse<void>>(`/dispute/${id}`);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to resolve a dispute
 * @param options - Hook options
 */
export function useResolveDispute(
  options: {
    onSuccess?: (data: ApiResponse<DisputeDTO>, variables: ResolveDisputeRequest) => void;
    onError?: (error: Error, variables: ResolveDisputeRequest) => void;
  } = {}
) {
  return useMutation<ApiResponse<DisputeDTO>, Error, ResolveDisputeRequest>({
    mutationFn: async ({ id, resolution }: ResolveDisputeRequest) => {
      if (!id) {
        throw new Error('Dispute ID is required');
      }
      const response = await riderApiClient.put<ApiResponse<DisputeDTO>>(
        `/dispute/${id}/resolve`,
        undefined,
        { params: { resolution } }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to reject a dispute
 * @param options - Hook options
 */
export function useRejectDispute(
  options: {
    onSuccess?: (data: ApiResponse<DisputeDTO>, variables: RejectDisputeRequest) => void;
    onError?: (error: Error, variables: RejectDisputeRequest) => void;
  } = {}
) {
  return useMutation<ApiResponse<DisputeDTO>, Error, RejectDisputeRequest>({
    mutationFn: async ({ id, resolution }: RejectDisputeRequest) => {
      if (!id) {
        throw new Error('Dispute ID is required');
      }
      const response = await riderApiClient.put<ApiResponse<DisputeDTO>>(
        `/dispute/${id}/reject`,
        undefined,
        { params: { resolution } }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to mark dispute as in progress
 * @param options - Hook options
 */
export function useMarkDisputeInProgress(
  options: {
    onSuccess?: (data: ApiResponse<DisputeDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<DisputeDTO>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await riderApiClient.put<ApiResponse<DisputeDTO>>(
        `/dispute/${id}/in-progress`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
